/** About panel — accordion with purpose, audience, usage, notices, and imprint.

Content is config-driven via ui_text keys. Mode-aware: the "how to use"
section adapts to the current map mode (habitats vs planning) and is only
shown when a municipality is selected (i.e. inside the app, not on the
landing page).
 */

import { useEffect, useId, useRef, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import { renderEmphasis } from "../utils/emphasis";
import type { MapMode, RegionConfig } from "../types";

interface AboutPanelProps {
  config: RegionConfig;
  /** Current map mode — controls which "how to use" text is shown.
   * When null (landing page), the how-to-use section is omitted entirely. */
  mode?: MapMode | null;
}

interface AccordionSection {
  key: string;
  title: string;
  body: React.ReactNode;
}

export function AboutPanel({ config, mode = null }: AboutPanelProps) {
  const { t } = useLanguageContext();
  const [open, setOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>("purpose");
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Outside-click dismissal — same pointerdown + .contains() pattern used by
  // LayerControls/ColorWheelPicker. Scoped to `open` only: it must NOT also
  // depend on `expandedSection`, or every accordion toggle tears down and
  // re-registers this listener on a delay, which is what caused the "any
  // click inside closes the panel" bug (a click on an accordion header can
  // land in the dead window between the old listener being removed and the
  // new one re-attaching, or interact badly with the removed setTimeout
  // delay + composedPath check that used to guard it).
  useEffect(() => {
    if (!open) return;
    const handlePointerOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerOutside);
    return () => document.removeEventListener("pointerdown", handlePointerOutside);
  }, [open]);

  // Escape: collapse an expanded section first, close the panel on a second press.
  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (expandedSection !== "purpose") setExpandedSection("purpose");
      else setOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, expandedSection]);

  const contact = config.branding.contact;

  const toggleSection = (key: string) => {
    setExpandedSection((previous) => (previous === key ? null : key));
  };

  const sections: AccordionSection[] = [
    {
      key: "purpose",
      title: t("purpose"),
      body: <p className="text-pretty leading-relaxed text-flima-slate/75">{renderEmphasis(t("purpose_text"))}</p>,
    },
    {
      key: "audience",
      title: t("target_audience"),
      body: (
        <p className="text-pretty leading-relaxed text-flima-slate/75">
          {t("target_audience_text")}
        </p>
      ),
    },
  ];

  // Mode-aware "how to use" — only shown inside the app (mode is not null)
  if (mode !== null) {
    const howToText = mode === "planning" ? t("how_to_use_planning") : t("how_to_use_habitats");
    sections.push({
      key: "howto",
      title: t("how_to_use"),
      body: <p className="text-pretty leading-relaxed text-flima-slate/75">{howToText}</p>,
    });
  }

  sections.push(
    {
      key: "notice",
      title: t("important_notice"),
      body: (
        <div className="space-y-2">
          <p className="break-words leading-relaxed text-flima-slate/80">{t("notice_text")}</p>
          <p className="break-words leading-relaxed text-flima-slate/80">
            {t("notice_coordinator")} {contact.name} ({contact.referat}){" "}
            {contact.email ? (
              <a
                href={`mailto:${contact.email}`}
                className="break-all font-semibold text-flima-river underline decoration-flima-river/30 underline-offset-2 hover:text-flima-spruce focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
              >
                {contact.email}
              </a>
            ) : null}
          </p>
        </div>
      ),
    },
    {
      key: "further",
      title: t("further_info"),
      body: (
        <ul className="space-y-2">
          <li>
            <a
              href={t("further_info_url")}
              target="_blank"
              rel="noopener noreferrer"
              className="break-words font-semibold text-flima-river underline decoration-flima-river/30 underline-offset-2 hover:text-flima-spruce focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
            >
              {t("further_info_text")}
            </a>
          </li>
          <li>
            <a
              href={t("further_info_url_2")}
              target="_blank"
              rel="noopener noreferrer"
              className="break-words font-semibold text-flima-river underline decoration-flima-river/30 underline-offset-2 hover:text-flima-spruce focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
            >
              {t("further_info_text_2")}
            </a>
          </li>
        </ul>
      ),
    },
    {
      key: "imprint",
      title: t("imprint"),
      body: (
        <div className="space-y-1.5 text-sm leading-relaxed text-flima-slate/75">
          <p className="font-semibold text-flima-spruce">{t("imprint_org")}</p>
          <p>{t("imprint_address")}</p>
          <p>{t("imprint_city")}</p>
          <p>{t("imprint_phone")}</p>
          <p>
            <a
              href={t("imprint_url")}
              target="_blank"
              rel="noopener noreferrer"
              className="break-words font-semibold text-flima-river underline decoration-flima-river/30 underline-offset-2 hover:text-flima-spruce focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
            >
              {t("imprint_url_label")}
            </a>
          </p>
          <p className="mt-2 text-xs text-flima-slate/55">{t("imprint_disclaimer")}</p>
        </div>
      ),
    },
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t("about")}
        className="flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-flima-spruce motion-reduce:transition-none"
      >
        <svg
          viewBox="0 0 20 20"
          className="h-5 w-5 sm:hidden"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 9v5m0-8h.01" />
        </svg>
        <span className="hidden sm:inline">{t("about")}</span>
        <svg
          viewBox="0 0 20 20"
          className={`h-4 w-4 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="m5.5 7.5 4.5 4 4.5-4" />
        </svg>
      </button>

      {open ? (
        <section
          id={panelId}
          aria-label={t("about")}
          className="absolute right-0 top-full z-40 mt-2 max-h-[min(38rem,calc(100dvh-6rem))] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain rounded-xl border border-flima-spruce/10 bg-white p-4 text-flima-slate shadow-panel"
        >
          <div className="border-l-4 border-flima-river pl-3">
            <h2 className="text-pretty font-display text-lg font-semibold text-flima-spruce">
              {config.branding.title}
            </h2>
          </div>

          <div className="mt-4 divide-y divide-flima-spruce/8">
            {sections.map((section) => {
              const isExpanded = expandedSection === section.key;
              return (
                <div key={section.key} className="py-1">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    aria-expanded={isExpanded}
                    className="flex w-full items-center justify-between gap-2 rounded-md py-2 text-left font-display text-sm font-semibold text-flima-spruce transition-colors hover:bg-flima-mist/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
                  >
                    <span>{section.title}</span>
                    <svg
                      viewBox="0 0 20 20"
                      className={`h-4 w-4 shrink-0 text-flima-slate/50 transition-transform motion-reduce:transition-none ${isExpanded ? "rotate-180" : ""}`}
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="m5.5 7.5 4.5 4 4.5-4" />
                    </svg>
                  </button>
                  {isExpanded ? (
                    <div className="pb-2 pl-1 pr-2 text-sm">{section.body}</div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <p className="mt-3 border-t border-flima-spruce/10 pt-3 text-xs text-flima-slate/50">
            {t("under_construction")}
          </p>
        </section>
      ) : null}
    </div>
  );
}
